import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { MediaAssetType } from '@/types/media';
import {
  getStockImages,
  getStockVideos,
  StockMediaAsset,
  StockMediaResponse,
} from '@/api/stock-media';

export type QueryStockMediaProps = {
  type: MediaAssetType;
  query: string;
};

const PAGE_SIZE = 30;
const TWO_MINUTES_IN_MILLIS = 120000;

const flattenPagesIntoList = (
  pages: StockMediaResponse[]
): StockMediaAsset[] => {
  return pages.flatMap((page) => page.items || []);
};

export const useStockMediaInfiniteQuery = (props: QueryStockMediaProps) => {
  const { query, type } = props;
  const queryVariables = {
    query,
    pageSize: PAGE_SIZE,
  };

  const { data, ...queryOptions } = useInfiniteQuery<StockMediaResponse>({
    queryKey: ['stock-media', type, queryVariables],
    enabled: !!query,
    queryFn: async ({ pageParam }) => {
      const queryFunction =
        type === MediaAssetType.image ? getStockImages : getStockVideos;
      const response = await queryFunction({
        page: pageParam as number,
        query,
        pageSize: PAGE_SIZE,
      });

      return response;
    },
    initialPageParam: 1,
    getNextPageParam: ({ hasNext, page }) => {
      return hasNext ? page + 1 : null;
    },
    staleTime: TWO_MINUTES_IN_MILLIS,
  });

  const media: StockMediaAsset[] = useMemo(() => {
    if (!data) {
      return [];
    }

    return flattenPagesIntoList(data.pages);
  }, [data]);

  return {
    media,
    ...queryOptions,
  };
};
